import { Group, Modal, Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "src/components/shared/Button/Button";
import { Select } from "src/components/shared/MantineCombobox";
import { useGetApi, useIsOnline } from "src/hooks";
import { mapGetSigners } from "src/pages/Settings/AdvancedFeatures/LogbookSigners/LogbookSigners.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSigningOverride } from "src/redux/slices/generatorSlice";
import { Flex } from "src/styled";
import { MEDLBID } from "src/utils";

const NOT_SET_VALUE = "__NOT_SET__";
const LOCKED_TO_ME_VALUE = "*SELF*";

const overrideToSelectValue = (override: string | null | undefined): string => {
  if (override == null || override === "") return NOT_SET_VALUE;
  return override;
};

const selectValueToOverride = (value: string | null): string | null => {
  if (!value || value === NOT_SET_VALUE) return null;
  return value;
};

interface Props {
  style?: React.CSSProperties;
}

const MedSigningOverrideSelect = ({ style }: Props) => {
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const isOnline = useIsOnline();
  const signingOverride = useSelector(
    state => state.generatorSlice.lbentry.signingOverride,
  );
  const isReadonly = useSelector(
    state => state.generatorSlice.lbentry.isReadonly,
  );
  const rolename = useSelector(state => state.permsSlice.rolename);
  const [opened, { open, close }] = useDisclosure(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);

  const { data } = useGetApi({
    request: "get-signers",
    mapper: mapGetSigners,
    fetchOnInit: logbookId === MEDLBID,
  });

  const [value, setValue] = useState<string>(
    overrideToSelectValue(signingOverride),
  );

  useEffect(() => {
    setValue(overrideToSelectValue(signingOverride));
  }, [signingOverride]);

  const medGroups = useMemo(
    () =>
      data?.rows?.filter(row => row.lbid === MEDLBID && row.name?.trim()) ?? [],
    [data?.rows],
  );

  const options = useMemo(() => {
    const base = [
      { value: NOT_SET_VALUE, label: "Not Set" },
      { value: LOCKED_TO_ME_VALUE, label: "Locked to me" },
    ];

    const medSigners = medGroups.map(row => ({
      value: row.name.trim(),
      label: row.name.trim(),
    }));

    const seen = new Set(base.map(o => o.value));
    const uniqueMedSigners = medSigners.filter(o => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });

    return [...base, ...uniqueMedSigners];
  }, [medGroups]);

  const locksOutUser = (next: string) => {
    const group = medGroups.find(row => row.name.trim() === next);
    if (!group || !rolename) return false;
    return ![...(group.pri ?? []), ...(group.sec ?? [])].includes(rolename);
  };

  const applyValue = (next: string) => {
    setValue(next);
    dispatch(setSigningOverride(selectValueToOverride(next)));
  };

  const handleChange = (v: string | null) => {
    const next = v ?? NOT_SET_VALUE;
    if (locksOutUser(next)) {
      setPendingValue(next);
      open();
      return;
    }
    applyValue(next);
  };

  const handleCancel = () => {
    setPendingValue(null);
    close();
  };

  const handleProceed = () => {
    if (pendingValue) applyValue(pendingValue);
    handleCancel();
  };

  if (logbookId !== MEDLBID) return null;

  return (
    <Flex gap={15} align="center" style={style}>
      <Text>Signing</Text>
      <Select
        data={options}
        value={value}
        onChange={handleChange}
        disabled={!isOnline || isReadonly}
        w={200}
      />
      <Modal
        opened={opened}
        onClose={handleCancel}
        title="Confirmation Required">
        <Text>
          You will no longer have access to this entry as you are setting the
          access to another person.
        </Text>
        <Group justify="right" mt={10}>
          <Button variant="default" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="filled" onClick={handleProceed}>
            Proceed
          </Button>
        </Group>
      </Modal>
    </Flex>
  );
};

export default MedSigningOverrideSelect;
